'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthToken, setAuthToken, clearAuthToken, getApiConfiguration } from '@/lib/api-adapter/config';
import { UpdateUserDto, UserEntity } from '@/lib/Apis/models';
import { UsersApi } from '@/lib/Apis/apis/users-api';
import { ProductsApi } from '@/lib/Apis/apis/products-api';
import { getDeviceId } from '@/lib/utils/deviceId';

interface AuthContextValue {
  user: UserEntity | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAuthReady: boolean;
  logout: () => Promise<void>;
  updateUser: (data: UpdateUserDto) => Promise<UserEntity>;
  refreshUser: () => Promise<void>;
  /** Store a token (+ optional user) from OTP verify or guest checkout. Calls refreshUser if no userData provided. */
  loginWithSession: (token: string, userData?: UserEntity) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserEntity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const getUserKey = () => {
    if (typeof window === 'undefined') return 'ecommerce_user';
    try {
      const config = require('@/lib/api-adapter/config').getCurrentConfig();
      return `ecommerce_user_${config?.storeId || 'default'}`;
    } catch {
      return 'ecommerce_user';
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (token) {
        const response = await new UsersApi(getApiConfiguration()).getMyProfile();
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
      setIsAuthReady(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem(getUserKey());
        if (cached) {
          setUser(JSON.parse(cached));
          setIsLoading(false);
        }
      } catch (_) {
        // ignore cache parse errors
      }
    }
    refreshUser();
  }, [refreshUser]);

  const loginWithSession = async (token: string, userData?: UserEntity): Promise<void> => {
    setAuthToken(token);
    // Order responses contain customer and payment details. Never retain them
    // across an account transition, even though order query keys are user-scoped.
    queryClient.removeQueries({ queryKey: ['order'] });
    queryClient.removeQueries({ queryKey: ['orders'] });
    queryClient.removeQueries({ queryKey: ['currentOrders'] });
    if (userData) {
      setUser(userData);
      if (typeof window !== 'undefined') {
        try {
          const config = require('@/lib/api-adapter/config').getCurrentConfig();
          localStorage.setItem(`ecommerce_user_${config?.storeId || 'default'}`, JSON.stringify(userData));
        } catch {
          localStorage.setItem('ecommerce_user', JSON.stringify(userData));
        }
      }
    } else {
      await refreshUser();
    }

    try {
      const deviceId = getDeviceId();
      if (deviceId) {
        await new ProductsApi(getApiConfiguration()).mergeAnonymousInterest({ deviceId });
      }
    } catch (error) {
      console.error('Failed to merge anonymous product interests:', error);
    }

    await queryClient.invalidateQueries({ queryKey: ['personalizedProducts'] });
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      clearAuthToken();
      setUser(null);
      queryClient.removeQueries({ queryKey: ['order'] });
      queryClient.removeQueries({ queryKey: ['orders'] });
      queryClient.removeQueries({ queryKey: ['currentOrders'] });
      if (typeof window !== 'undefined') {
        localStorage.removeItem(getUserKey());
      }
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (data: UpdateUserDto): Promise<UserEntity> => {
    setIsLoading(true);
    try {
      const response = await new UsersApi(getApiConfiguration()).updateMyProfile(data);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to update user:', error);
      await refreshUser();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isAuthReady,
    logout,
    updateUser,
    refreshUser,
    loginWithSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
